import React, {useMemo, useState} from 'react'

import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import {useUserContext} from "../../../contexts/UserContext";
import {useNavigate} from "react-router";
import {useShallow} from "zustand/react/shallow";
import {usePricingsStore} from "../../../stores/pricingsStore";
import {useDebounce} from "@uidotdev/usehooks";
import useDidMountEffect from "../../../hooks/useDidMountEffect";

import {PricingListRowMobile, PRICING_MONEY_FIELDS} from "./PricingListRowMobile";

import './PricingListPageMobile.scss'

// pricingData is keyed year -> { [modelName]: trims[], __has_pricings } ; each
// trim is { id, name, pricings: [{ id, description, msrp }] }. Keys prefixed
// with "__" are metadata flags, never real model names — filter them out.
const isMetaKey = (key) => key.startsWith('__')

const countPricings = (trims) =>
    trims.reduce((sum, trim) => sum + (trim.pricings?.length || 0), 0)

// ── A trim: its existing pricings (full editable cards) plus a "create new"
// card — same capabilities as the desktop pricing rows. ──
const TrimBlock = ( { trim } ) => {
    const pricings = trim.pricings || []
    return (
        <div className="pim-trim">
            <div className="pim-trim__head">
                <span className="pim-trim__name">{trim.name}</span>
                <span className="pim-trim__count">{pricings.length} pricing{pricings.length === 1 ? '' : 's'}</span>
            </div>
            <div className="pim-prow-head">
                {PRICING_MONEY_FIELDS.map((field) => (
                    <span key={field.key}>{field.label}</span>
                ))}
            </div>
            <div className="pim-trim__rows">
                <PricingListRowMobile key={`${trim.id}-new`} trim={trim}/>
                {pricings.map((pricing) => (
                    <PricingListRowMobile key={pricing.id} trim={trim} pricing={pricing}/>
                ))}
            </div>
        </div>
    )
}

// ── Collapsible model row ──
const ModelSection = ( { modelName, trims, expanded, onToggle, pricingsOnly } ) => {
    const visibleTrims = pricingsOnly ? trims.filter((t) => (t.pricings?.length || 0) > 0) : trims
    const pricingCount = countPricings(trims)

    return (
        <div className="pim-model">
            <button type="button" className="pim-model__head" onClick={onToggle}>
                {expanded ? <ExpandMoreIcon/> : <ChevronRightIcon/>}
                <span className="pim-model__name">{modelName}</span>
                {pricingCount > 0 && <span className="pim-model__badge">{pricingCount}</span>}
            </button>
            {
                expanded &&
                <div className="pim-model__body">
                    {
                        visibleTrims.length > 0
                            ? visibleTrims.map((trim) => (
                                <TrimBlock key={trim.id} trim={trim}/>
                            ))
                            : <div className="pim-model__empty">No trims to show</div>
                    }
                </div>
            }
        </div>
    )
}

// ── Collapsible year section ──
const YearSection = ( { year, models, expanded, onToggle, openModels, toggleModel, pricingsOnly } ) => {
    const pricingCount = models.reduce((sum, [, trims]) => sum + countPricings(trims), 0)

    return (
        <div className="pim-year">
            <button type="button" className="pim-year__head" onClick={onToggle}>
                <span className="pim-year__chevron">
                    {expanded ? <ExpandMoreIcon/> : <ChevronRightIcon/>}
                </span>
                <span className="pim-year__label">{year}</span>
                {pricingCount > 0 && <span className="pim-year__badge">{pricingCount} pricing{pricingCount === 1 ? '' : 's'}</span>}
            </button>
            {
                expanded &&
                <div className="pim-year__body">
                    {models.map(([modelName, trims]) => (
                        <ModelSection
                            key={modelName}
                            modelName={modelName}
                            trims={trims}
                            pricingsOnly={pricingsOnly}
                            expanded={openModels.has(`${year}::${modelName}`)}
                            onToggle={() => toggleModel(year, modelName)}
                        />
                    ))}
                </div>
            }
        </div>
    )
}

export const PricingListPageMobile = ( { make } ) => {
    const { profileState } = useUserContext()
    const navigate = useNavigate()

    const {
        pricingData,
        isLoading,
        search,
        setSearch,
    } = usePricingsStore(
        useShallow((state) => ({
            pricingData: state.pricingData,
            isLoading: state.isLoading,
            search: state.search,
            setSearch: state.setSearch,
        }))
    )

    // Local search field mirrors the store; debounced writes drive the parent's
    // server-side refetch (same mechanism as the desktop search header).
    const [searchValue, setSearchValue] = useState(search || '')
    const debouncedSearch = useDebounce(searchValue, 400)
    useDidMountEffect(function pushSearchToStore() {
        setSearch(debouncedSearch)
    }, [debouncedSearch])

    const isSearching = (searchValue || '').trim().length > 0

    const [openYears, setOpenYears] = useState(() => new Set())
    const [openModels, setOpenModels] = useState(() => new Set())
    const [pricingsOnly, setPricingsOnly] = useState(false)

    const toggleYear = (year) => {
        setOpenYears((prev) => {
            const next = new Set(prev)
            next.has(year) ? next.delete(year) : next.add(year)
            return next
        })
    }

    const toggleModel = (year, modelName) => {
        const key = `${year}::${modelName}`
        setOpenModels((prev) => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }

    const handleMakeChange = (nextMake) => {
        setOpenYears(new Set())
        setOpenModels(new Set())
        navigate(`/pricing/${nextMake}`)
    }

    // Build a sorted year -> [ [modelName, trims], ... ] structure, dropping
    // meta keys and (when "pricings only" is on) years/models with no pricings.
    const years = useMemo(() => {
        const data = pricingData || {}
        return Object.keys(data)
            .filter((year) => !isMetaKey(year))
            .sort((a, b) => Number(b) - Number(a))
            .map((year) => {
                const models = Object.entries(data[year] || {})
                    .filter(([modelName]) => !isMetaKey(modelName))
                    .filter(([, trims]) => Array.isArray(trims))
                return { year, models }
            })
            .map(({ year, models }) => ({
                year,
                models: pricingsOnly
                    ? models.filter(([, trims]) => countPricings(trims) > 0)
                    : models,
            }))
            .filter(({ models }) => models.length > 0)
    }, [pricingData, pricingsOnly])

    const totalPricings = useMemo(
        () => years.reduce((sum, { models }) =>
            sum + models.reduce((s, [, trims]) => s + countPricings(trims), 0), 0),
        [years]
    )

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="pim-state">
                    <CircularProgress size={26}/>
                </div>
            )
        }

        if (years.length === 0) {
            return (
                <div className="pim-state pim-state--empty">
                    {
                        isSearching
                            ? <>No results for “{searchValue.trim()}”.</>
                            : pricingsOnly
                                ? <>No saved pricings for this brand yet.</>
                                : <>No vehicles found for this brand.</>
                    }
                </div>
            )
        }

        return years.map(({ year, models }) => (
            <YearSection
                key={year}
                year={year}
                models={models}
                pricingsOnly={pricingsOnly}
                // Auto-expand everything while searching so results are visible.
                expanded={isSearching || openYears.has(year)}
                onToggle={() => toggleYear(year)}
                openModels={openModels}
                toggleModel={toggleModel}
            />
        ))
    }

    return (
        <Box id="PricingListPageMobile">
            <div className="pim-header">
                <div className="pim-header__top">
                    <h3 className="pim-title">Pricing Index</h3>
                    <Select
                        className="pim-make-select"
                        value={make}
                        onChange={(e) => handleMakeChange(e.target.value)}
                        MenuProps={{ disableScrollLock: true }}
                    >
                        {profileState?.makes_list?.map((m) => (
                            <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                    </Select>
                </div>

                <div className="pim-search">
                    <SearchIcon className="pim-search__icon"/>
                    <input
                        type="text"
                        value={searchValue}
                        placeholder="Search model or trim"
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                    {
                        searchValue.length > 0 &&
                        <button
                            type="button"
                            className="pim-search__clear"
                            aria-label="Clear search"
                            onClick={() => setSearchValue('')}
                        >
                            <CloseIcon/>
                        </button>
                    }
                </div>

                <div className="pim-toolbar">
                    <span className="pim-toolbar__count">
                        {totalPricings} saved pricing{totalPricings === 1 ? '' : 's'}
                    </span>
                    <button
                        type="button"
                        className={`pim-toggle${pricingsOnly ? ' pim-toggle--on' : ''}`}
                        onClick={() => setPricingsOnly((v) => !v)}
                    >
                        Pricings only
                    </button>
                </div>
            </div>

            <div className="pim-list">
                {renderBody()}
            </div>
        </Box>
    )
}
